<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.producer_prices.submit')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <div
        v-if="mergeNotice"
        class="alert alert-warning py-2 mb-3"
        role="status"
        data-testid="prices-merge-notice"
      >
        <template v-if="mergeNotice.shipped">
          {{ $t("forwards.deals.producer_prices.shipped_meanwhile") }}
        </template>
        <template v-else-if="delivery === null">
          {{ $t("forwards.deals.producer_prices.delivery_gone") }}
        </template>
        <template v-else>
          <p v-if="mergeUnaffected" class="mb-0">
            {{ $t("forwards.deals.merge.unaffected") }}
          </p>
          <p v-else class="mb-1">
            {{ $t("forwards.deals.merge.changed_meanwhile") }}
          </p>
          <ul v-if="!mergeUnaffected" class="mb-0 ps-3">
            <li v-if="mergeNotice.takenOver > 0">
              {{ $t("forwards.deals.merge.taken_over", mergeNotice.takenOver) }}
            </li>
            <li v-if="mergeNotice.conflicts > 0">
              {{ $t("forwards.deals.merge.conflicts", mergeNotice.conflicts) }}
            </li>
            <li v-if="mergeNotice.linesRemoved > 0">
              {{
                $t(
                  "forwards.deals.producer_prices.lines_removed",
                  mergeNotice.linesRemoved
                )
              }}
            </li>
          </ul>
        </template>
      </div>

      <div v-if="delivery" class="p-1">
        <p class="text-body-tertiary">
          {{ $t("forwards.deals.producer_prices.body") }}
        </p>

        <div class="mb-3">
          <label class="form-label" :for="defaultPriceId">
            {{ $t("forwards.deals.producer_prices.default_price") }}
          </label>
          <p v-if="isReadOnly" class="mb-0" data-testid="default-price">
            {{ readOnlyDefaultPrice }}
          </p>
          <DraftConflictComponent
            v-else-if="conflicts[DEFAULT_PRICE_FIELD]"
            :mine="defaultPriceText(conflicts[DEFAULT_PRICE_FIELD].mine)"
            :theirs="defaultPriceText(conflicts[DEFAULT_PRICE_FIELD].theirs)"
            @pick="pick(DEFAULT_PRICE_FIELD, $event)"
          />
          <input
            v-else
            :id="defaultPriceId"
            v-model="draft[DEFAULT_PRICE_FIELD]"
            type="number"
            step="0.001"
            class="form-control form-control-sm default-price-input"
            :placeholder="delivery.unit_price ?? ''"
            data-testid="default-price-input"
          />
          <div class="form-text">
            {{ $t("forwards.deals.producer_prices.default_price_help") }}
          </div>
        </div>

        <p v-if="rows.length === 0" class="text-body-tertiary fst-italic mb-0">
          {{ $t("forwards.deals.producer_prices.no_lines") }}
        </p>

        <table v-else class="table table-sm mb-0">
          <thead>
            <tr>
              <th scope="col">
                {{ $t("forwards.deals.producer_prices.company") }}
              </th>
              <th scope="col" class="text-end">
                {{ $t("forwards.deals.producer_prices.amount") }}
              </th>
              <th scope="col" class="price-cell">
                {{ $t("forwards.deals.producer_prices.price") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.companyId"
              :data-testid="row.hasLine ? 'line-row' : 'unescrowed-row'"
            >
              <td>
                {{ row.companyName }}
                <div
                  v-if="!row.hasLine && row.poolsText"
                  class="text-body-tertiary fs-10"
                >
                  {{ row.poolsText }}
                </div>
              </td>
              <td class="text-end">
                <template v-if="row.hasLine">
                  {{ formatNumberWithSpaces(row.amount) }}
                </template>
                <span
                  v-else-if="row.keptOutsidePools"
                  class="badge text-bg-warning"
                  :title="
                    $t('forwards.deals.producer_prices.kept_outside_pools_help')
                  "
                >
                  {{ $t("forwards.deals.producer_prices.kept_outside_pools") }}
                </span>
                <span v-else class="badge text-bg-light">
                  {{ $t("forwards.deals.producer_prices.no_escrow_yet") }}
                </span>
              </td>
              <td class="price-cell">
                <span v-if="isReadOnly">{{ row.savedPrice ?? "" }}</span>
                <DraftConflictComponent
                  v-else-if="conflicts[row.field]"
                  :mine="priceText(conflicts[row.field].mine)"
                  :theirs="priceText(conflicts[row.field].theirs)"
                  @pick="pick(row.field, $event)"
                />
                <div v-else class="d-flex align-items-center gap-1">
                  <input
                    v-model="draft[row.field]"
                    type="number"
                    step="0.01"
                    class="form-control form-control-sm"
                    :placeholder="fallbackPrice ?? ''"
                    :aria-label="
                      $t('forwards.deals.producer_prices.price_for', {
                        company: row.companyName,
                      })
                    "
                  />
                  <button
                    v-if="!row.hasLine"
                    type="button"
                    class="btn btn-sm btn-link text-secondary px-1"
                    :aria-label="
                      $t('forwards.deals.producer_prices.remove_producer', {
                        company: row.companyName,
                      })
                    "
                    data-testid="remove-producer"
                    @click="removeProducer(row.field)"
                  >
                    <FontAwesomeIcon icon="fa-solid fa-xmark" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <p v-if="rows.length > 0" class="text-body-tertiary fs-10 mt-2 mb-0">
          {{ deliveryPriceNote }}
        </p>

        <div v-if="!isReadOnly" class="mt-3" data-testid="add-producer">
          <div class="form-label">
            {{ $t("forwards.deals.producer_prices.add_producer") }}
          </div>
          <SearchableSelectComponent
            v-model="pickedCompanyId"
            :options="eligibleOptions"
            :isLoading="eligibleLoading"
            :isError="eligibleFailed"
            :errorMessage="$t('forwards.deals.producer_prices.eligible_error')"
            :placeholder="
              $t('forwards.deals.producer_prices.add_producer_placeholder')
            "
            size="sm"
          />
          <div class="form-text">
            {{ $t("forwards.deals.producer_prices.add_producer_help") }}
          </div>
        </div>

        <div class="mt-3">
          <div class="form-label">
            {{ $t("forwards.deals.producer_prices.trade_owner") }}
          </div>
          <p v-if="isReadOnly" class="mb-0" data-testid="trade-owner">
            {{ readOnlyTradeOwner }}
          </p>
          <DraftConflictComponent
            v-else-if="conflicts[TRADE_OWNER_FIELD]"
            :mine="tradeOwnerText(conflicts[TRADE_OWNER_FIELD].mine)"
            :theirs="tradeOwnerText(conflicts[TRADE_OWNER_FIELD].theirs)"
            @pick="pick(TRADE_OWNER_FIELD, $event)"
          />
          <SearchableSelectComponent
            v-else
            v-model="tradeOwnerCompanyId"
            :options="tradeOwnerOptions"
            :placeholder="
              $t('forwards.deals.producer_prices.trade_owner_placeholder')
            "
          />
          <div class="form-text">
            {{ $t("forwards.deals.producer_prices.trade_owner_help") }}
          </div>
        </div>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{
          isReadOnly
            ? $t("forwards.deals.common.close")
            : $t("forwards.deals.common.cancel")
        }}
      </button>
      <ActionButtonWithSpinner
        v-if="!isReadOnly"
        :busy="busy"
        :disabled="busy || hasConflicts"
        variant="btn-primary"
        @click="submit"
      >
        {{ $t("forwards.deals.producer_prices.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useSwapReferrers } from "@/composables/useSwapReferrers";
import DraftConflictComponent from "@/forwards2/deals/DraftConflictComponent.vue";
import type { DraftConflict } from "@/forwards2/deals/draftMerge";
import {
  companyOfField,
  DEFAULT_PRICE_FIELD,
  isLineField,
  lineField,
  linedCompanyIds,
  mergeProducerPrices,
  type ProducerPricesDraft,
  type ProducerPriceValue,
  producerPricesRequest,
  savedProducerPrices,
  TRADE_OWNER_FIELD,
  valueText,
} from "@/forwards2/deals/producerPricesDraft";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import { forwardsApi } from "@/forwards2/forwardsApi";
import type {
  DeliveryResponse,
  EligibleProducer,
  ProducerPricesRequest,
} from "@/forwards2/forwardsTypes";
import { groupBy, omit, sortBy } from "lodash-es";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * One producer as the dialog lists it; its own price lives in the draft under `field`.
 *
 * @property hasLine - False for a producer priced before its escrow arrived.
 * @property poolsText - The backing pools it belongs to and their floors, for one without a line.
 * @property keptOutsidePools - A saved price of a producer that has left every backing pool.
 */
interface PriceRow {
  companyId: number;
  companyName: string;
  amount: number;
  field: string;
  savedPrice: string | null;
  hasLine: boolean;
  poolsText: string | null;
  keptOutsidePools: boolean;
}

/** What the dialog says after a 409 merge, until it closes. */
interface MergeNotice {
  takenOver: number;
  conflicts: number;
  linesRemoved: number;
  shipped: boolean;
}

/**
 * What each producer selling into one delivery invoices at — staff-only (spec, UI -> Gating and
 * visibility): a super admin sets the prices here, and a sales admin reads them (`readOnly`, user
 * decision 2026-09-23).
 *
 * The form states the delivery's prices as a whole: every line is on screen, so a row left blank
 * means "invoice this producer at the default producer price" — or at the delivery's own price when
 * there is no default — rather than "leave whatever was there"; a typed price is the producer's own
 * even when it equals the default, and stays put when the default moves. The default also prices the
 * producers whose certificates reach the delivery later, so it can be set before any line exists:
 * it is how a sale whose producers share an averaged price pays them that average.
 *
 * A member of a pool backing the delivery can be given its own price before its escrow arrives: the
 * picker lists those members, and such a producer shows as "no escrow yet" until its line appears.
 * Removing it drops its own price. A producer that left every backing pool keeps its saved price,
 * listed as "not in a backing pool": resent with the rest it stays, and applies again if the
 * producer comes back; removed, it is gone. A pool refuses a new member whose own price is below
 * its floor, so such a price may need raising here first.
 * The pool floors behind the lines are the backend's to enforce; its refusal names the producer,
 * the pool and the figure.
 *
 * The trade owner — paid commission on the swap margin — is saved with the prices and stated whole
 * too: a cleared select clears it. Lines only exist once escrow arrives, but the trade owner can be
 * set before that, so the dialog opens on a delivery with no lines at all (user decision 2026-09-28).
 *
 * The save carries the `plan_version` the dialog was opened with. When someone else saved
 * meanwhile, the parent reloads the deal and calls `mergeSavedPrices`: the draft is kept and merged
 * per line and for the trade owner onto what is saved now (`producerPricesDraft`), as the schedule
 * card merges. A delivery that shipped meanwhile turns the dialog read-only.
 */
const props = withDefaults(
  defineProps<{
    delivery: DeliveryResponse | null;
    dealId: number;
    /** `DealResponse.plan_version`, the stamp a save is composed against. */
    planVersion: number;
    readOnly?: boolean;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { readOnly: false, busy: false, errorMessage: null }
);

const emit = defineEmits<{
  submit: [request: ProducerPricesRequest];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();
const deliveryWindowText = useDeliveryWindowText();
const defaultPriceId = useId();

const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};

const draft = ref<ProducerPricesDraft>({});
/** The saved values the draft is composed against, and their `plan_version`. */
const base = ref<ProducerPricesDraft>({});
const baseVersion = ref(0);
const conflicts = ref<Record<string, DraftConflict<ProducerPriceValue>>>({});
const mergeNotice = ref<MergeNotice | null>(null);

const { options: candidateOptions, fetchCandidates } = useSwapReferrers();

const eligible = ref<EligibleProducer[]>([]);
const eligibleLoading = ref(false);
const eligibleFailed = ref(false);
let eligibleRequest = 0;

const isReadOnly = computed(
  () =>
    props.readOnly ||
    props.delivery === null ||
    mergeNotice.value?.shipped === true
);

/** Someone else's save touched nothing in this dialog (another delivery, say): only Save again is needed. */
const mergeUnaffected = computed(
  () =>
    mergeNotice.value !== null &&
    mergeNotice.value.takenOver === 0 &&
    mergeNotice.value.conflicts === 0 &&
    mergeNotice.value.linesRemoved === 0
);

/** A read-only viewer needs the backing pools only to describe the producers priced before escrow. */
const hasUnescrowed = computed(
  () => props.delivery?.producer_lines.some((line) => !line.has_line) ?? false
);

const hasConflicts = computed(() => Object.keys(conflicts.value).length > 0);

/** Per company, its backing pools and the floor each snapshotted. */
const eligibleByCompany = computed(() =>
  groupBy(eligible.value, (producer) => producer.company_id)
);

const poolsText = (companyId: number): string | null => {
  const pools = eligibleByCompany.value[companyId] ?? [];
  if (pools.length === 0) return null;
  return pools
    .map((pool) =>
      pool.minimum_price === null
        ? pool.pool_name
        : t("forwards.deals.producer_prices.pool_floor", {
            pool: pool.pool_name,
            price: pool.minimum_price,
          })
    )
    .join(", ");
};

const companyName = (companyId: number): string =>
  props.delivery?.producer_lines.find((line) => line.company_id === companyId)
    ?.company_name ??
  eligibleByCompany.value[companyId]?.[0]?.company_name ??
  String(companyId);

const rows = computed<PriceRow[]>(() => {
  const delivery = props.delivery;
  if (delivery === null) return [];
  const lined = linedCompanyIds(delivery);
  const lineRows = delivery.producer_lines
    .filter((line) => line.has_line)
    .map((line) => ({
      companyId: line.company_id,
      companyName: line.company_name ?? String(line.company_id),
      amount: line.amount,
      field: lineField(line.company_id),
      savedPrice: line.unit_price,
      hasLine: true,
      poolsText: null,
      keptOutsidePools: false,
    }));
  const unescrowedRows = Object.keys(draft.value)
    .filter((field) => isLineField(field) && !lined.has(companyOfField(field)))
    .map((field) => {
      const companyId = companyOfField(field);
      const saved = delivery.producer_lines.find(
        (line) => line.company_id === companyId
      );
      return {
        companyId,
        companyName: companyName(companyId),
        amount: 0,
        field,
        savedPrice: saved?.unit_price ?? null,
        hasLine: false,
        poolsText: poolsText(companyId),
        keptOutsidePools: saved?.kept_outside_pools ?? false,
      };
    });
  return [...lineRows, ...sortBy(unescrowedRows, (row) => row.companyName)];
});

/** The backing pools' members not on the form yet, once each, with their pools and floors. */
const eligibleOptions = computed(() =>
  Object.values(eligibleByCompany.value)
    .filter(([producer]) => !(lineField(producer.company_id) in draft.value))
    .map(([producer]) => ({
      value: producer.company_id,
      label: producer.company_name,
      description: poolsText(producer.company_id) ?? undefined,
    }))
);

/** The picker only adds a row, so it reads empty again once a producer is picked. */
const pickedCompanyId = computed({
  get: () => null,
  set: (companyId: number | null | undefined) => {
    if (companyId === null || companyId === undefined) return;
    draft.value[lineField(companyId)] = null;
  },
});

const tradeOwnerCompanyId = computed({
  get: () => {
    const value = draft.value[TRADE_OWNER_FIELD];
    return typeof value === "number" ? value : null;
  },
  set: (companyId: number | null | undefined) => {
    draft.value[TRADE_OWNER_FIELD] = companyId ?? null;
  },
});

const windowText = computed(() =>
  props.delivery === null ? "" : deliveryWindowText(props.delivery)
);

const title = computed(() =>
  t("forwards.deals.producer_prices.title", { window: windowText.value })
);

/** The draft's default producer price, null while it is blank. */
const draftDefaultPrice = computed(() =>
  valueText(draft.value[DEFAULT_PRICE_FIELD])
);

/** What a blank line invoices at: the draft's default, else the delivery's own price. */
const fallbackPrice = computed(
  () => draftDefaultPrice.value ?? props.delivery?.unit_price ?? null
);

const deliveryPriceNote = computed(() => {
  if (draftDefaultPrice.value !== null)
    return t("forwards.deals.producer_prices.default_price_note", {
      price: t("forwards.deals.plan.unit_price", {
        price: draftDefaultPrice.value,
      }),
    });
  return t("forwards.deals.producer_prices.delivery_price_note", {
    price:
      props.delivery?.unit_price === null ||
      props.delivery?.unit_price === undefined
        ? t("forwards.deals.plan.no_price")
        : t("forwards.deals.plan.unit_price", {
            price: props.delivery.unit_price,
          }),
  });
});

const readOnlyDefaultPrice = computed(() => {
  const price = props.delivery?.default_producer_price ?? null;
  return price === null
    ? t("forwards.deals.producer_prices.default_price_none")
    : t("forwards.deals.plan.unit_price", { price });
});

/** The standing trade owner stays selectable even if it is no longer a candidate, so it is not lost silently. */
const tradeOwnerOptions = computed(() => {
  const current = props.delivery?.trade_owner;
  if (
    current === null ||
    current === undefined ||
    candidateOptions.value.some((option) => option.value === current.company_id)
  )
    return candidateOptions.value;
  return [
    ...candidateOptions.value,
    {
      value: current.company_id,
      label: current.company_name ?? `#${current.company_id}`,
    },
  ];
});

const readOnlyTradeOwner = computed(() => {
  const current = props.delivery?.trade_owner;
  if (current === null || current === undefined)
    return t("forwards.deals.producer_prices.trade_owner_none");
  return current.company_name ?? `#${current.company_id}`;
});

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (!isOpen) return;
  base.value =
    props.delivery === null ? {} : savedProducerPrices(props.delivery);
  draft.value = { ...base.value };
  baseVersion.value = props.planVersion;
  conflicts.value = {};
  mergeNotice.value = null;
  if (!props.readOnly && candidateOptions.value.length === 0)
    void fetchCandidates();
  loadEligible();
});

/** The backing pools' members as the delivery stands now; a reply to an earlier request is dropped. */
const loadEligible = () => {
  eligible.value = [];
  eligibleRequest += 1;
  if (props.delivery !== null && (!props.readOnly || hasUnescrowed.value))
    void fetchEligible(props.delivery.id, eligibleRequest);
};

const fetchEligible = async (deliveryId: number, request: number) => {
  eligibleLoading.value = true;
  eligibleFailed.value = false;
  try {
    const producers = (
      await forwardsApi.listEligibleProducers(props.dealId, deliveryId)
    ).data.producers;
    if (request === eligibleRequest) eligible.value = producers;
  } catch {
    // The picker says it failed; the rest of the dialog still saves.
    if (request === eligibleRequest) eligibleFailed.value = true;
  } finally {
    if (request === eligibleRequest) eligibleLoading.value = false;
  }
};

const removeProducer = (field: string) => {
  draft.value = omit(draft.value, field);
  conflicts.value = omit(conflicts.value, field);
};

const priceText = (value: ProducerPriceValue) =>
  valueText(value) ??
  (draftDefaultPrice.value === null
    ? t("forwards.deals.producer_prices.blank_price")
    : t("forwards.deals.producer_prices.blank_follows_default"));

const defaultPriceText = (value: ProducerPriceValue) =>
  valueText(value) ?? t("forwards.deals.producer_prices.blank_price");

const tradeOwnerText = (value: ProducerPriceValue) => {
  if (typeof value !== "number")
    return t("forwards.deals.producer_prices.trade_owner_none");
  return (
    tradeOwnerOptions.value.find((option) => option.value === value)?.label ??
    `#${value}`
  );
};

const pick = (field: string, side: "mine" | "theirs") => {
  const conflict = conflicts.value[field];
  if (conflict === undefined) return;
  draft.value[field] = conflict[side];
  conflicts.value = omit(conflicts.value, field);
};

const close = () => {
  model.value = false;
};

const submit = () => {
  if (props.delivery === null || hasConflicts.value) return;
  emit("submit", producerPricesRequest(draft.value, baseVersion.value));
};

/**
 * Merges the draft onto the delivery as the props now show it, after the parent reloaded the deal
 * for a stale save. What is saved now becomes the base, so the next Save retries against it.
 */
const mergeSavedPrices = () => {
  const latest = props.delivery;
  if (latest === null) {
    mergeNotice.value = {
      takenOver: 0,
      conflicts: 0,
      linesRemoved: 0,
      shipped: false,
    };
    return;
  }
  const merged = mergeProducerPrices(base.value, draft.value, latest);
  draft.value = merged.draft;
  conflicts.value = merged.shipped ? {} : merged.conflicts;
  base.value = savedProducerPrices(latest);
  baseVersion.value = props.planVersion;
  // The plan may have changed what backs the delivery, and with it who may be priced and at what floor.
  loadEligible();
  mergeNotice.value = {
    takenOver: merged.takenOver,
    conflicts: Object.keys(conflicts.value).length,
    linesRemoved: merged.linesRemoved,
    shipped: merged.shipped,
  };
};

defineExpose({ mergeSavedPrices });
</script>

<style scoped lang="scss">
.price-cell {
  width: 10rem;
}

.default-price-input {
  max-width: 10rem;
}
</style>
